import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { map, mergeMap, Observable, of, Subject, Subscription, tap } from 'rxjs';
import { ConfigResourceService, GenericResourceService } from '@mri/app/resources';
import { AnyObject, Position } from '@mri/types';
import { ta } from 'date-fns/locale';
import { ChangeDetection } from '@angular/cli/lib/config/workspace-schema';

enum PositionError {
  PREV_ELEMENT_INCORRECT = 'prevElementIncorrect',
  TRASHED = 'trashed',
  TOTAL = 'total',
}

interface DiagnosticPosition {
  errors: Partial<Record<PositionError, Position[]>>;
  avgReport: number;
  unReportCount: number;
  avgReportReal: number;
}

function diffReport(startTime: string | Date, endTime: string | Date): number {
  let diff = 0;
  if(endTime){
    diff = new Date(endTime).getTime() - new Date(startTime).getTime();
  }
  return diff;
}

interface PositionExt extends Position{
  errors?: PositionError[];
}

@Component({
  selector: 'app-diagnostic-position',
  templateUrl: './diagnostic-position.component.html',
  styleUrl: './diagnostic-position.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DiagnosticPositionComponent implements OnDestroy {
  private _sub = new Subscription();
  private _mobileId$ = new Subject<string>();
  private _day: string;
  private _mobileId: string;

  isLoading = false;
  diagnostic!: DiagnosticPosition ;
  positions: Array<PositionExt > = [];

  @Input() set mobileId(value: string){
    this._mobileId = value;
    this._mobileId$.next(value);
  }
  @Input() set day(value: string){
    this._day = value;
    this._mobileId$.next(this._mobileId);
  };

  constructor(
    private _configSrv: ConfigResourceService,
    private _genericSrv: GenericResourceService,
    private _cv: ChangeDetectorRef
  ) {
    this._sub.add(
      this._mobileId$
        .pipe(
          mergeMap(mobileId => this._getPositionsFromMobileId(mobileId)),
          tap(() => _cv.markForCheck()),
          tap(() => _cv.detectChanges()),

        ).subscribe()
    )
  }

  ngOnDestroy() {
    this._sub.unsubscribe();
  }

  private _getPositionsFromMobileId(mobileId: string): Observable<PositionExt[]> {
    if(!this._mobileId || !this._day){
      return of([]);
    }
    return this._getPosition({ mobileId });
  }

  private _getPosition(initialFilter: AnyObject): Observable<PositionExt[]>{
    this.isLoading = true;

    console.log('DAY', this._day);
    return this._configSrv.getCalcVars()
      .pipe(
        map( config => new Date(new Date(`${this._day}T00:00:00-00:00`).getTime() + ((config?.diffHours || 0)))),
        map( startDeviceTime => ({
          ...initialFilter,
          startDeviceTime, endDeviceTime: new Date(startDeviceTime.getTime() + 1000 * 60 * 60 * 24),
          trashed: [true, false]
        })),
        tap(filter => console.log('filter', filter)),
        mergeMap( filter => this._genericSrv.getElements<Position>('positions', {filter, paginate: { page: 1, size: 999999}})),
        map( response => response.objects),
        tap(positions => this.positions = positions),
        tap( positions => this._calculateDiagnostic(positions)),
        tap(() => this.isLoading = false)
      )
  }

  private _calculateDiagnostic(positions: PositionExt[]){
    const diagnostic: DiagnosticPosition = { errors: {}, avgReport: NaN, avgReportReal: NaN, unReportCount: 0, };

    let prevPosition: PositionExt;

    const positionSorted =  positions
      .sort((a, b) => new Date(a.deviceTime).getTime() - new Date(b.deviceTime).getTime());

    positionSorted.forEach((position) => {
        if(position.trashed){
          diagnostic.errors[PositionError.TRASHED] = [
            ...(diagnostic.errors[PositionError.TRASHED] ?? []),
            position
          ]
          position.errors = [...(position.errors ?? []), PositionError.TRASHED]
        } else {
          if(prevPosition && prevPosition._id !== position.prev?._id){
            diagnostic.errors[PositionError.PREV_ELEMENT_INCORRECT] = [
              ...(diagnostic.errors[PositionError.PREV_ELEMENT_INCORRECT] ?? []),
              position
            ]
            position.errors = [...(position.errors ?? []), PositionError.PREV_ELEMENT_INCORRECT]
          }
        }

        if(!position.trashed){
          prevPosition = position;
        }
      });

    diagnostic.errors[PositionError.TOTAL] = positions.filter(v => (v.errors?.length ?? 0) > 0);

    const positionAccepted = positionSorted
      .filter(p => !p.trashed );

    const acceptedSum = positionAccepted.reduce(
      (sum, p) => {
        const diff = diffReport(prevPosition.deviceTime, p.deviceTime);
        prevPosition = p;
        return sum + diff;
      }, 0)

    diagnostic.avgReport =  acceptedSum / positionAccepted.length;


    const sum = positions.reduce(
      (sum, p) => {
        const diff = diffReport(prevPosition.deviceTime, p.deviceTime);
        prevPosition = p;
        return sum + diff;
      }, 0)

    diagnostic.avgReportReal =  sum / positions.length;

    this.diagnostic = diagnostic;
  }





}
